/*
=========================================================
* Material Kit 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

function ExperienceSection() {
  const lightColor = "#505050ff";
  const darkColor = "#222222ff";

  return (
    <MKBox
      bgColor="#343434"
      borderRadius="xl"
      shadow="lg"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      mt={{ xs: 10, sm: 10, md: 10 }}
      mb={{ xs: 10, sm: 10, md: 10 }}
      mx={3}
    >
      <MKBox
        variant="gradient"
        bgColor="info"
        coloredShadow="secondary"
        borderRadius="lg"
        p={2}
        mx={2}
        mt={-3}
      >
        <MKTypography
          variant="h1"
          color="white"
          sx={{ overflowWrap: "break-word", textAlign: "center" }}
        >
          Experience
        </MKTypography>
      </MKBox>
      <MKBox p={3}>
        <MKBox bgColor={lightColor} borderRadius="lg" p={1} pb={5} pt={2}>
          <MKTypography variant="h4" color="white">
            Senior Engineer, Skymap Games
          </MKTypography>
          <MKTypography variant="body2" color="white" mb={1} fontStyle="italic">
            Sept 2025 – Current; Madison, WI.
          </MKTypography>
          <MKTypography variant="body2" color="white" pl={1} mb={1}>
            Description coming soon.
          </MKTypography>
        </MKBox>
        <MKBox bgColor={darkColor} borderRadius="lg" p={1} pb={5} pt={2}>
          <MKTypography variant="h4" color="white">
            Gameplay Engineer, Zenimax Online Studios
          </MKTypography>
          <MKTypography variant="body2" color="white" mb={1} fontStyle="italic">
            May 2024 – Sept 2025; Madison, WI.
          </MKTypography>
          <MKTypography variant="body2" color="white" pl={1} mb={1}>
            Delivered core game and engine features within major milestones for internal review,
            corporate stakeholder review, and launch. Implemented key C++ systems and engine
            workflows used critically by other teams. Trailblazed investigations into software
            products solving collaboration issues across a globally distributed team of 400+,
            producing recommendations advising project leads on solutions contributing directly to
            project-wide decisions influencing developer workflows.
          </MKTypography>
          <List sx={{ listStyleType: "disc", paddingLeft: "40px" }}>
            <ListItem sx={{ display: "list-item" }}>
              Authored native C++ systems supporting server/client communication in modern C++
              codebases
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Evaluated core preproduction systems for modernization, generated tasks with phased
              timeline, and refactored code resulting in more performant, elegant, and extendable
              systems
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Implemented data structures and algorithms including Red Black Trees and Weighted Edge
              Graphs
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Developed critical animation team pipeline data containers and importing logic
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Wrote supporting unit test suites using Microsoft Unit Testing Framework for C++
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Configured Microsoft Azure servers to conduct playtests with developers in the U.S.
              and U.K.
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Obtained multiple C++ certifications outside of core work hours
            </ListItem>
          </List>
        </MKBox>
        <MKBox bgColor={lightColor} borderRadius="lg" p={1} pb={5} pt={2}>
          <MKTypography variant="h4" color="white">
            Game Programmer, Roundhouse Studios
          </MKTypography>
          <MKTypography variant="body2" color="white" mb={1} fontStyle="italic">
            Nov 2019 – May 2024; Madison, WI.
          </MKTypography>
          <MKTypography variant="body2" color="white" pl={1} mb={1}>
            Built several gameplay systems in native C++ within Unreal and proprietary game engines.
            Iterated on component and system design alongside designers to deliver dynamic systems
            reusable for world building and combat implementation.
          </MKTypography>
          <List sx={{ listStyleType: "disc", paddingLeft: "40px" }}>
            <ListItem sx={{ display: "list-item" }}>
              Developed environmental hazards (explosives, shock, damage zones) and player combat
              abilities in native C++ within Unreal Engine for Redfall (PC/Xbox Series, 2023)
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Implemented core damage instigation system for notifying game systems of character
              damage, type, source, etc.
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Created additional gameplay systems in a proprietary game engine using modern C++
              (including fast travel, player respawn, and checkpoints)
            </ListItem>
          </List>
        </MKBox>
        <MKBox bgColor={darkColor} borderRadius="lg" p={1} pb={5} pt={2}>
          <MKTypography variant="h4" color="white">
            Gameplay Programmer, Human Head Studios
          </MKTypography>
          <MKTypography variant="body2" color="white" mb={1} fontStyle="italic">
            May 2018 – Nov 2019; Madison, WI.
          </MKTypography>
          <MKTypography variant="body2" color="white" pl={1} mb={1}>
            Contributed to the development and release of online games with over 1 million users.
            Developed web-based administration tools for a live service product backed by a
            relational database. Integrated game services with external partner RESTful web
            services. Drove user interface (UI) and user experience (UX) design alongside artists.
            Implemented all UI/UX along with additional gameplay systems for a shipped online title.
          </MKTypography>
          <List sx={{ listStyleType: "disc", paddingLeft: "40px" }}>
            <ListItem sx={{ display: "list-item" }}>
              Built web-based tools in C#, ASP.NET, and SQL for data administration, aggregation,
              and reporting used in a production environment by customer support
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Developed Python scripts load balancing users on cloud servers and batching feedback
              data
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Championed direction and full implementation of UI/UX on a shipped title (Rune 2)
              using Unreal UMG
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Implemented server/client game systems in C# and visual scripting languages
            </ListItem>
          </List>
        </MKBox>
        <MKBox bgColor={lightColor} borderRadius="lg" p={1} pb={5} pt={2}>
          <MKTypography variant="h4" color="white">
            Lead Engineer / Owner, Subject Matter Games
          </MKTypography>
          <MKTypography variant="body2" color="white" mb={1} fontStyle="italic">
            April 2014 – Nov 2019; Madison, WI.
          </MKTypography>
          <MKTypography variant="body2" color="white" pl={1} mb={1}>
            Took two game ideas from idea through production and launch, responsible for all
            gameplay systems, art, asset integration, and additional audio creation. Utilized Unity
            and C# to build a procedurally generated infinite runner and a narrative-driven
            adventure title. Created all promotional artwork and videos, and promoted games at local
            conventions in person.
          </MKTypography>
          <List sx={{ listStyleType: "disc", paddingLeft: "40px" }}>
            <ListItem sx={{ display: "list-item" }}>
              Authored procedural generation, art, animations, and store integrations for the
              infinite runner Super Wall Crash (iOS/Android, 2016)
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Wrote dialogue scripts and recorded voice over for Don&apos;t Be Dinner (Windows
              Store, 2014)
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Participated in game james with completed small games and produced additional
              prototypes using Unity/C#
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Promoted titles at local conventions/user group meetups including Midwest Gaming
              Classic by setting up a table and interacting with players
            </ListItem>
          </List>
        </MKBox>
        <MKBox bgColor={darkColor} borderRadius="lg" p={1} pb={5} pt={2}>
          <MKTypography variant="h5" color="white">
            Software Engineer, Sentry insurance
          </MKTypography>
          <MKTypography variant="body2" color="white" mb={1} fontStyle="italic">
            May 2009 – May 2018; Stevens Point, WI.
          </MKTypography>
          <MKTypography variant="body2" color="white" pl={1} mb={1}>
            Developed crucial web implementations of state-specific business logic in a web-based
            policy and user administration system used across all company U.S. agencies. Implemented
            front-end webpages and supporting database structures. Acted as a technical lead,
            mentoring junior engineers and interns on project tasks. Gathered requirements with
            various business units and demoed implementation of new features to non-technical
            business units. Supported products outside of core hours as part of an on-call
            production support team.
          </MKTypography>
          <List sx={{ listStyleType: "disc", paddingLeft: "40px" }}>
            <ListItem sx={{ display: "list-item" }}>
              Delivered front-end webpages enforcing state-specific transportation regulation rules
              using Java and Gosu
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Built relational database schemas for object model and wrote SQL scripts to solve
              schema versioning issues
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Manually merged code across branches to solve conflicts across teams in SVN
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Implemented RabbitMQ as a backend messaging queue replacement for an outdated system
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Mentored engineering interns by providing direction, delegating tasks, and reviewing
              output
            </ListItem>
            <ListItem sx={{ display: "list-item" }}>
              Earned technical certifications for multiple Guidewire products and completed several
              internal insurance training courses
            </ListItem>
          </List>
        </MKBox>
      </MKBox>
    </MKBox>
  );
}

export default ExperienceSection;
